import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody
} from '@mui/material';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { tenantApiFetch } from './tenantApi';

function defaultRange() {
  const to = new Date();
  const from = new Date(to.getTime() - 30 * 86400000);
  return { from: from.toISOString().slice(0, 10), to: to.toISOString().slice(0, 10) };
}

function mergeSeries(usageByDay, loginsByDay) {
  const map = new Map();
  for (const r of usageByDay || []) {
    map.set(r.date, {
      date: r.date,
      apiRequests: r.apiRequests,
      loginRollup: r.authLoginsRollup,
      auditRollup: r.auditEventsRollup,
      loginsExact: 0
    });
  }
  for (const r of loginsByDay || []) {
    const row = map.get(r.date) || {
      date: r.date,
      apiRequests: 0,
      loginRollup: 0,
      auditRollup: 0,
      loginsExact: 0
    };
    row.loginsExact = r.logins;
    map.set(r.date, row);
  }
  return [...map.values()].sort((a, b) => a.date.localeCompare(b.date));
}

export default function TenantAnalyticsPage() {
  const [{ from, to }, setRange] = useState(defaultRange);
  const [summary, setSummary] = useState(null);
  const [series, setSeries] = useState([]);
  const [userActivity, setUserActivity] = useState(null);
  const [auditSum, setAuditSum] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const q = useMemo(() => `from=${from}&to=${to}`, [from, to]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [sRes, tRes, uRes, aRes] = await Promise.all([
        tenantApiFetch(`/api/tenant/analytics/summary?${q}`),
        tenantApiFetch(`/api/tenant/analytics/timeseries?${q}`),
        tenantApiFetch(`/api/tenant/analytics/user-activity?${q}`),
        tenantApiFetch(`/api/tenant/analytics/audit-summary?${q}`)
      ]);
      const sJson = await sRes.json().catch(() => ({}));
      const tJson = await tRes.json().catch(() => ({}));
      const uJson = await uRes.json().catch(() => ({}));
      const aJson = await aRes.json().catch(() => ({}));
      if (!sRes.ok) throw new Error(sJson.error || 'Summary failed');
      if (!tRes.ok) throw new Error(tJson.error || 'Timeseries failed');
      if (!uRes.ok) throw new Error(uJson.error || 'User activity failed');
      if (!aRes.ok) throw new Error(aJson.error || 'Audit summary failed');
      setSummary(sJson);
      setSeries(mergeSeries(tJson.usageByDay, tJson.loginsByDay));
      setUserActivity(uJson);
      setAuditSum(aJson);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [q]);

  useEffect(() => {
    load();
  }, [load]);

  const k = summary && summary.kpis;

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Analytics & usage
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Scope: <strong>this tenant only</strong>. Range uses UTC dates. API request counts reflect authenticated
        tenant API calls after instrumentation is enabled (see KPI notes in API response).
      </Typography>

      <Paper sx={{ p: 2, mb: 2, display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
        <TextField
          label="From"
          type="date"
          size="small"
          InputLabelProps={{ shrink: true }}
          value={from}
          onChange={(e) => setRange((r) => ({ ...r, from: e.target.value }))}
        />
        <TextField
          label="To"
          type="date"
          size="small"
          InputLabelProps={{ shrink: true }}
          value={to}
          onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))}
        />
        <Button variant="outlined" onClick={load} disabled={loading}>
          Refresh
        </Button>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading && !summary ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6} sm={4} md={2}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  Active users
                </Typography>
                <Typography variant="h6">{k?.activeUsers ?? '—'}</Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  Logins (range)
                </Typography>
                <Typography variant="h6">{k?.loginsInRange ?? '—'}</Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  Distinct users w/ login
                </Typography>
                <Typography variant="h6">{k?.distinctUsersWithLoginInRange ?? '—'}</Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  Tenant API reqs
                </Typography>
                <Typography variant="h6">{k?.tenantApiRequestsInRange ?? '—'}</Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  New users
                </Typography>
                <Typography variant="h6">{k?.newUsersInRange ?? '—'}</Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  Audit rows
                </Typography>
                <Typography variant="h6">{k?.auditRowsExactInRange ?? '—'}</Typography>
              </Paper>
            </Grid>
          </Grid>

          <Grid container spacing={2}>
            <Grid item xs={12} lg={7}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Activity by day
                </Typography>
                <ResponsiveContainer width="100%" height={320}>
                  <LineChart data={series}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="apiRequests" name="API requests" stroke="#1976d2" dot={false} />
                    <Line type="monotone" dataKey="loginsExact" name="Logins (exact)" stroke="#2e7d32" dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </Paper>
            </Grid>
            <Grid item xs={12} lg={5}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Audit actions (range)
                </Typography>
                <ResponsiveContainer width="100%" height={320}>
                  <BarChart
                    data={
                      auditSum && auditSum.byAction && auditSum.byAction.length
                        ? auditSum.byAction
                        : [{ action: '—', count: 0 }]
                    }
                    layout="vertical"
                    margin={{ left: 8 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" />
                    <YAxis type="category" dataKey="action" width={120} tick={{ fontSize: 10 }} />
                    <Tooltip />
                    <Bar dataKey="count" name="Events" fill="#ed6c02" />
                  </BarChart>
                </ResponsiveContainer>
              </Paper>
            </Grid>
            <Grid item xs={12}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="subtitle1" gutterBottom>
                  User activity (logins in range)
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Email</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell align="right">Logins</TableCell>
                      <TableCell>Last login</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {((userActivity && userActivity.users) || []).map((u) => (
                      <TableRow key={u.id}>
                        <TableCell>{u.email}</TableCell>
                        <TableCell>{u.name || '—'}</TableCell>
                        <TableCell align="right">{u.loginEventsInRange}</TableCell>
                        <TableCell>{u.lastLoginAt ? String(u.lastLoginAt) : '—'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Paper>
            </Grid>
          </Grid>
        </>
      )}
    </Box>
  );
}
